"use client";

import { useTranslations } from "next-intl";

import {
  useMatchTimeline,
  type MatchTimelineEvent,
} from "@/entities/replay-metrics";

/**
 * KAN-786: лента live-событий игры в разборе матча.
 *
 * Источник — события Live Client Data API, собранные Tauri-клиентом участника во
 * время игры (замена закрытому payload-реверсу, ADR-008). Есть только события с
 * игровым временем: киллы, объекты, ace. Кастов, вардов и позиций в источнике нет.
 *
 * Матч без событий (никто из участников не запускал desktop-клиент) — блок не
 * рисуется вовсе: пустая лента читалась бы как «в игре ничего не происходило».
 */

interface MatchTimelineFeedProps {
  matchId: string;
}

/** Типы, которые есть смысл показывать строкой ленты. */
const VISIBLE_EVENTS = new Set([
  "ChampionKill",
  "FirstBlood",
  "Multikill",
  "Ace",
  "TurretKilled",
  "InhibKilled",
  "DragonKill",
  "BaronKill",
  "HeraldKill",
  "HordeKill",
  "AtakhanKill",
  "FirstBrick",
  "GameEnd",
]);

function formatClock(timeSeconds: number): string {
  const total = Math.max(0, Math.floor(timeSeconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

interface ParsedEvent {
  killer?: string;
  victim?: string;
  assisters: string[];
  dragonType?: string;
  stolen: boolean;
  killStreak?: number;
}

/** Достаёт из сырого события участников. Битый JSON — просто без деталей. */
function parseEvent(event: MatchTimelineEvent): ParsedEvent {
  try {
    const data = JSON.parse(event.dataJson) as Record<string, unknown>;
    const str = (k: string) => (typeof data[k] === "string" ? (data[k] as string) : undefined);
    const killStreak = data["KillStreak"];
    return {
      killer: str("KillerName") ?? str("Acer"),
      victim: str("VictimName") ?? str("TurretKilled") ?? str("InhibKilled"),
      assisters: Array.isArray(data["Assisters"]) ? (data["Assisters"] as string[]) : [],
      dragonType: str("DragonType"),
      stolen: data["Stolen"] === "True" || data["Stolen"] === true,
      killStreak: typeof killStreak === "number" ? killStreak : undefined,
    };
  } catch {
    return { assisters: [], stolen: false };
  }
}

/** Имя без тег-лайна: в событиях Live Client имена приходят как "Name #TAG". */
function shortName(name: string | undefined): string {
  if (!name) return "";
  return (name.split("#")[0] ?? name).trim();
}

function EventRow({ event }: { event: MatchTimelineEvent }) {
  const t = useTranslations("Bracket.modals.matchTimeline.events");
  const parsed = parseEvent(event);

  let text: string;
  switch (event.name) {
    case "ChampionKill":
    case "FirstBlood":
      text = t(event.name === "FirstBlood" ? "firstBlood" : "championKill", {
        killer: shortName(parsed.killer),
        victim: shortName(parsed.victim),
      });
      break;
    case "Multikill":
      text = t("multikill", {
        killer: shortName(parsed.killer),
        count: parsed.killStreak ?? 2,
      });
      break;
    case "Ace":
      text = t("ace", { killer: shortName(parsed.killer) });
      break;
    case "TurretKilled":
    case "FirstBrick":
      text = t("turretKilled", { killer: shortName(parsed.killer) });
      break;
    case "InhibKilled":
      text = t("inhibKilled", { killer: shortName(parsed.killer) });
      break;
    case "DragonKill":
      text = t(parsed.stolen ? "dragonStolen" : "dragonKill", {
        killer: shortName(parsed.killer),
      });
      break;
    case "BaronKill":
      text = t(parsed.stolen ? "baronStolen" : "baronKill", {
        killer: shortName(parsed.killer),
      });
      break;
    case "HeraldKill":
      text = t("heraldKill", { killer: shortName(parsed.killer) });
      break;
    case "HordeKill":
      text = t("hordeKill", { killer: shortName(parsed.killer) });
      break;
    case "AtakhanKill":
      text = t("atakhanKill", { killer: shortName(parsed.killer) });
      break;
    case "GameEnd":
      text = t("gameEnd");
      break;
    default:
      text = event.name;
  }

  return (
    <li className="flex items-baseline gap-2 text-sm">
      <span className="w-12 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums">
        {formatClock(event.timeSeconds)}
      </span>
      <span className="text-foreground">{text}</span>
    </li>
  );
}

export function MatchTimelineFeed({ matchId }: MatchTimelineFeedProps) {
  const t = useTranslations("Bracket.modals.matchTimeline");
  const { data } = useMatchTimeline(matchId);

  const games = (data?.games ?? [])
    .map((g) => ({
      ...g,
      events: g.events.filter((e) => VISIBLE_EVENTS.has(e.name)),
    }))
    .filter((g) => g.events.length > 0);

  if (games.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h4 className="mb-3 text-sm font-semibold text-foreground">{t("title")}</h4>
      <div className="flex flex-col gap-4">
        {games.map((game, index) => (
          <div key={game.gameId}>
            {games.length > 1 && (
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                {t("game", { number: index + 1 })}
              </div>
            )}
            <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
              {game.events.map((event) => (
                <EventRow key={event.eventId} event={event} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
